import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  ExitToApp as LogoutIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
  EmojiEvents as RankingIcon,
  PictureAsPdf as PdfIcon,
  WorkspacePremium as CertificateIcon,
  SportsTennis as TennisIcon,
  SportsHockey as IceHockeyIcon,
  SportsSoccer as FootballIcon,
  SportsHandball as BadmintonIcon,
  SportsCricket as CricketIcon,
  Sports as AthleticsIcon,
  TrendingUp as TrendingUpIcon,
  School as LearningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Circle as CircleIcon,
  FitnessCenter as FitnessIcon,
  Description as DescriptionIcon,
  Compare as CompareIcon,
  AutoAwesome as RecommendationsIcon,
  SmartToy as AiIcon,
  Shield as ShieldIcon,
  Download as DownloadIcon,
  DeleteForever as DeleteIcon,
  NotificationsOff as WithdrawIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import EncycdataLogo from '@components/layout/EncycdataLogo';
import PlayerSidebar from '@components/layout/PlayerSidebar';
import { toast } from 'react-toastify';
import PlayerProfile from '@components/PlayerProfile';
import BillingInvoicesPanel from '@components/billing/BillingInvoicesPanel';
import RankingEvaluation from '@components/performance/RankingEvaluation';
import { generatePerformanceReportPDF } from '@utils/generatePerformanceReportPDF';
import { generateRankingEvaluationReportPDF } from '@utils/generateRankingEvaluationReportPDF';
import { generateDashboardReportCardPDF } from '@utils/generateDashboardReportCardPDF';
import PlayerCertificates from '@components/certificates/PlayerCertificates';
import { CORE_SPORTS, getDefaultRankingSubcategory, isShootingSport } from '@utils/sports';

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 72;

/** Parse JSON from response without throwing on empty/invalid body (e.g. 500 with no body). */
async function safeJson<T = any>(response: Response): Promise<{ success: boolean; data?: T; message?: string }> {
  const text = await response.text();
  if (!text.trim()) {
    return {
      success: false,
      message: response.status >= 500
        ? 'Cannot reach server. Is the backend running on port 5000?'
        : (response.statusText || 'Empty response'),
    };
  }
  try {
    return JSON.parse(text) as { success: boolean; data?: T; message?: string };
  } catch {
    return { success: false, message: 'Invalid response' };
  }
}

// Helper function to get grade color
const getGradeColor = (grade: string): string => {
  switch (grade) {
    case 'A+':
      return '#4caf50'; // Green
    case 'A':
      return '#2196f3'; // Blue
    case 'B':
      return '#ff9800'; // Orange
    case 'C':
    default:
      return '#f44336'; // Red
  }
};

// ─── My Data & Privacy Tab ───────────────────────────────────────────────────
const MyDataPrivacyTab: React.FC = () => {
  const [loading, setLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [consentMarketing, setConsentMarketing] = useState<boolean | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const apiCall = async (method: string, path: string, body?: object) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    const resp = await fetch(`/api${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.message || resp.statusText);
    }
    return resp;
  };

  const handleExport = async () => {
    setLoading('export');
    setMessage(null);
    try {
      const resp = await apiCall('GET', '/user/data-export');
      const data = await resp.json();
      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'Your data has been downloaded.' });
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || 'Export failed.' });
    } finally {
      setLoading(null);
    }
  };

  const handleWithdrawMarketing = async () => {
    setLoading('consent');
    setMessage(null);
    try {
      await apiCall('POST', '/user/withdraw-consent', { consent_marketing: false });
      setConsentMarketing(false);
      setMessage({ type: 'success', text: 'Marketing consent withdrawn. You will no longer receive promotional emails.' });
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || 'Failed to update consent.' });
    } finally {
      setLoading(null);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteDialogOpen(false);
    setLoading('delete');
    setMessage(null);
    try {
      await apiCall('DELETE', '/user/account');
      setMessage({
        type: 'info',
        text: 'Your account has been scheduled for deletion within 30 days. You will be logged out shortly.',
      });
      setTimeout(() => { window.location.href = '/login'; }, 4000);
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || 'Failed to request deletion.' });
    } finally {
      setLoading(null);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f4c81', mb: 1 }}>
        My Data &amp; Privacy
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        EncycGames is committed to your privacy under GDPR, COPPA, and India's DPDP Act.
        You can export, manage, or delete your data at any time.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Data Export */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <DownloadIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Download My Data</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Export all personal data we hold about you — profile, consent history, submissions — as a JSON file (GDPR Art.20).
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={loading === 'export'}
                sx={{ bgcolor: '#0f4c81', '&:hover': { bgcolor: '#0a3660' } }}
              >
                {loading === 'export' ? 'Preparing…' : 'Download My Data'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Consent Management */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <WithdrawIcon color="warning" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Manage Consent</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Terms &amp; Data Processing:</strong> Required for using the platform. To revoke, delete your account below.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Marketing emails:</strong> Optional — you can withdraw this anytime.
              </Typography>
              {consentMarketing === false ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                  <CheckCircleIcon fontSize="small" />
                  <Typography variant="body2">Marketing consent withdrawn.</Typography>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<WithdrawIcon />}
                  onClick={handleWithdrawMarketing}
                  disabled={loading === 'consent'}
                >
                  {loading === 'consent' ? 'Updating…' : 'Withdraw Marketing Consent'}
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Policy Links */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <ShieldIcon color="info" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Our Policies</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <a href="/privacy-policy" target="_blank" rel="noreferrer" style={{ color: '#0f4c81' }}>
                  Privacy Policy &amp; GDPR/COPPA notice
                </a>
              </Typography>
              <Typography variant="body2">
                <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#0f4c81' }}>
                  Terms of Service
                </a>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Data queries: <strong>privacy@encycgames.com</strong>
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Delete Account */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 3, borderColor: 'error.light' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <DeleteIcon color="error" />
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>Delete My Account</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Permanently delete your account and all personal data within 30 days (GDPR Art.17 right to erasure). This action cannot be undone.
              </Typography>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                disabled={loading === 'delete'}
              >
                {loading === 'delete' ? 'Processing…' : 'Request Account Deletion'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete confirmation dialog */}
      {deleteDialogOpen && (
        <Box
          sx={{
            position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setDeleteDialogOpen(false)}
        >
          <Paper
            sx={{ p: 4, maxWidth: 420, mx: 2, borderRadius: 3 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main', mb: 1 }}>
              Confirm Account Deletion
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your account and all data will be permanently deleted within 30 days.
              You will be logged out immediately. This cannot be undone.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" color="error" onClick={handleDeleteAccount}>
                Yes, Delete My Account
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

const PlayerDashboard: React.FC = () => {
  const { user, logout, setUser } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showEmailVerifiedMessage, setShowEmailVerifiedMessage] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  
  // Coach selection state
  const [selectedCoach, setSelectedCoach] = useState<string>('self');
  const [coachOptions, setCoachOptions] = useState<Array<{id: string, name: string}>>([]);
  
  // Player data state
  const [player, setPlayer] = useState<any>({
    // Do not use user.id as player id — wait for /api/players/me (avoids wrong UUID in dashboard API calls).
    id: (user as any)?.playerId || '',
    userId: user?.id || '',
    playerIdCode: (user as any)?.playerIdCode || 'N/A',
    dateOfBirth: null,
    gender: null,
    level: null,
    ageCategory: null,
    primarySport: null,
    secondarySport: null,
    country: null,
    academyName: null,
    phone: null,
    createdAt: new Date(),
    updatedAt: new Date(),
    user: {
      id: user?.id || '',
      firstName: (user as any)?.firstName || '',
      lastName: (user as any)?.lastName || '',
      email: user?.email || '',
      role: user?.role,
      isActive: (user as any)?.isActive || true,
    }
  });

  // Training and ranking reports state
  const [trainingReports, setTrainingReports] = useState<any[]>([]);
  const [rankingReports, setRankingReports] = useState<any[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [linkedCoachNameById, setLinkedCoachNameById] = useState<Record<string, string>>({});
  
  // Ranking scores state for dashboard
  const [rankingScores, setRankingScores] = useState<Record<string, Record<string, any>>>({});
  const [rankingScoresBySubcategory, setRankingScoresBySubcategory] = useState<Array<{
    sport: string;
    subcategory: string;
    subcategoryLabel: string;
    'self-evaluation': any;
    'coach-academy': any;
    'practical-test': any;
  }>>([]);
  const [loadingRankingScores, setLoadingRankingScores] = useState(false);

  // Learning module state
  const [learningExpanded, setLearningExpanded] = useState<Record<string, boolean>>({});

  const [playerLoadError, setPlayerLoadError] = useState<string | null>(null);

  // Load player details
  const loadPlayerDetails = async () => {
    if (!user?.id) return;
    setPlayerLoadError(null);
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`/api/players/me?userId=${user.id}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      const data = await safeJson(response);
      if (data.success && data.data) {
        const d = data.data;
        // Normalize so PlayerProfile always has player.user (API returns flat firstName, lastName, email)
        setPlayer({
          ...d,
          userId: d.userId || user?.id,
          user: {
            id: d.userId || user?.id,
            firstName: d.firstName ?? d.user?.firstName,
            lastName: d.lastName ?? d.user?.lastName,
            email: d.email ?? d.user?.email,
            role: d.role ?? d.user?.role,
            isActive: d.isActive ?? d.user?.isActive ?? true,
          },
        });
        if (user && d.userId && (d.playerIdCode || d.canAddChildren != null)) {
          setUser({
            ...user,
            id: d.userId || user.id,
            playerId: d.id || user.playerId,
            playerIdCode: d.playerIdCode || user.playerIdCode,
            firstName: d.user?.firstName ?? d.firstName ?? user.firstName,
            lastName: d.user?.lastName ?? d.lastName ?? user.lastName,
            email: d.email ?? d.user?.email ?? user.email,
            canEditProfile: d.canEditProfile ?? user.canEditProfile,
            canAddChildren: d.canAddChildren ?? user.canAddChildren,
            canCreateChild: d.canCreateChild ?? user.canCreateChild,
            maritalStatus: d.maritalStatus ?? user.maritalStatus,
            parentRole: d.parentRole ?? user.parentRole,
            parentRoleSaved: d.parentRoleSaved ?? user.parentRoleSaved,
            intendedChildCount: d.intendedChildCount ?? user.intendedChildCount,
            needsFamilyOnboarding: d.needsFamilyOnboarding ?? user.needsFamilyOnboarding,
            isChildInCustody: d.isChildInCustody ?? user.isChildInCustody,
            gender: d.gender ?? user.gender,
            dateOfBirth: d.dateOfBirth ?? user.dateOfBirth,
            ageCategory: d.ageCategory ?? user.ageCategory,
            children: d.children ?? user.children,
          });
        }
      } else {
        if (response.status === 401) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }
        const msg = response.status === 401
          ? 'Session expired or invalid. Please log in again.'
          : (data.message || 'Failed to load player details.');
        setPlayerLoadError(msg);
        console.error('Failed to load player details:', data.message);
      }
    } catch (err: any) {
      const msg = err?.message?.includes('fetch') ? 'Cannot reach server. Is the backend running on port 5000?' : (err?.message || 'Failed to load player details.');
      setPlayerLoadError(msg);
      console.error('Error loading player details:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load training reports - only show SUBMITTED evaluations
  const loadTrainingReports = async () => {
    if (!player.id) return;
    
    try {
      setLoadingReports(true);
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`/api/performance/basic/player/${player.id}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      const data = await safeJson(response);
      if (response.ok && data.success && data.data) {
        // Filter only SUBMITTED evaluations
        const submittedReports = data.data.filter((report: any) => {
          const isSubmitted = report.submitted === true || report.submitted === 'true' || report.submitted === 1;
          return isSubmitted;
        });
        
        console.log('[Dashboard] Filtered training reports:', {
          total: data.data.length,
          submitted: submittedReports.length
        });
        
        // Map reports and add sport information
        const reportsWithSport = submittedReports.map((report: any) => {
          const reportSport = report.sport || player.primarySport || 'Badminton';
          return {
            ...report,
            sport: reportSport,
            isPrimarySport: reportSport === player.primarySport,
            isSecondarySport: reportSport === player.secondarySport,
          };
        });
        setTrainingReports(reportsWithSport);
      }
    } catch (error) {
      console.error('Error loading training reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  // Load ranking scores for dashboard
  const loadRankingScores = async () => {
    if (!player.id) return;
    
    try {
      setLoadingRankingScores(true);
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`/api/ranking-evaluations/dashboard/${player.id}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      const data = await safeJson(response);
      if (response.ok && data.success && data.data) {
        console.log('[Dashboard] Received ranking scores data:', data.data);
        console.log('[Dashboard] Badminton self-evaluation:', data.data['Badminton']?.['self-evaluation']);
        setRankingScores(data.data);
        const bySubcategory = (data as any)?.bySubcategory;
        setRankingScoresBySubcategory(Array.isArray(bySubcategory) ? bySubcategory : []);
      } else {
        console.log('[Dashboard] No ranking scores data received:', data);
      }
    } catch (error) {
      console.error('Error loading ranking scores:', error);
    } finally {
      setLoadingRankingScores(false);
    }
  };

  // Load ranking reports
  const loadRankingReports = async () => {
    if (!player.id) return;
    
    try {
      // v2 (reportId-based) – preferred
      const v2Res = await fetch(`/api/ranking-reports/player/${player.id}`);
      const v2Data = await v2Res.json().catch(() => null);

      if (v2Res.ok && v2Data?.success && Array.isArray(v2Data.data) && v2Data.data.length > 0) {
        const categoryLabels: { [key: string]: string } = {
          'self-evaluation': 'Self Evaluation',
          'coach-academy': 'Coach/Academy Evaluation',
          'practical-test': 'Board Exam',
        };

        const v2Reports = v2Data.data.map((r: any) => ({
          reportId: r.reportId,
          categoryId: r.categoryId,
          categoryLabel: categoryLabels[r.categoryId] || r.categoryId,
          // v2 computed fields (from backend)
          score: Number(r.score || 0),
          totalStrokes: Number(r.totalStrokes || r.ratedStrokes || 0),
          maxScore: Number(r.maxScore || 0),
          percentage: String(r.percentage || '0.0'),
          grade: String(r.grade || 'C'),
          gradeDescription: String(r.gradeDescription || ''),
          evaluationDate: r.periodEndDate || null, // legacy UI uses this to label period
          periodKey: r.periodKey,
          hasData: true,
          sport: r.sport || player.primarySport || 'Badminton',
          isPrimarySport: (r.sport || player.primarySport) === player.primarySport,
        }));

        setRankingReports(v2Reports);
        return;
      }

      // Legacy fallback (non-breaking)
      const categories = ['self-evaluation', 'coach-academy', 'practical-test'];
      const reports: any[] = [];

      for (const categoryId of categories) {
        try {
          const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
          const response = await fetch(`/api/ranking-evaluations/summary/${categoryId}/player/${player.id}`, {
            headers: {
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            }
          });
          const data = await safeJson(response);
          if (response.ok && data.success && data.data) {
            const categoryLabels: { [key: string]: string } = {
              'self-evaluation': 'Self Evaluation',
              'coach-academy': 'Coach/Academy Evaluation',
              'practical-test': 'Board Exam',
            };

            const reportObj = {
              categoryId,
              categoryLabel: categoryLabels[categoryId] || categoryId,
              score: data.data.score || 0,
              totalStrokes: data.data.totalStrokes || 0,
              maxScore: data.data.maxScore || 0,
              percentage: data.data.percentage || '0.0',
              grade: data.data.grade || 'C',
              gradeDescription: data.data.gradeDescription || '',
              evaluationDate: data.data.evaluationDate || null,
              hasData: data.data.totalStrokes > 0,
              sport: player.primarySport || 'Badminton',
              isPrimarySport: true,
            };

            if (reportObj.hasData) reports.push(reportObj);
          }
        } catch (error) {
          console.error(`Error loading ranking for ${categoryId}:`, error);
        }
      }

      setRankingReports(reports);
    } catch (error) {
      console.error('Error loading ranking reports:', error);
    }
  };

  // Sync activeTab from URL pathname for direct links
  useEffect(() => {
    const path = location.pathname;
    if (path === '/player/profile') setActiveTab('profile');
    else if (path === '/player/children' || path === '/player/family') setActiveTab('profile');
    else if (path === '/player/invoices') setActiveTab('invoices');
    else if (path === '/player/certificates') setActiveTab('certificates');
    else if (path === '/player/privacy') setActiveTab('privacy');
    else if (path === '/player/dashboard' || path === '/player') setActiveTab('dashboard');
  }, [location.pathname]);

  // Check for email verification success message
  useEffect(() => {
    const emailVerified = searchParams.get('emailVerified');
    if (emailVerified === 'true') {
      setShowEmailVerifiedMessage(true);
      toast.success('Your email has been verified successfully!', { autoClose: 5000 });
      // Remove query param from URL
      searchParams.delete('emailVerified');
      setSearchParams(searchParams, { replace: true });
      // Hide message after 5 seconds
      setTimeout(() => {
        setShowEmailVerifiedMessage(false);
      }, 5000);
    }
  }, [searchParams, setSearchParams]);

  // Load player details on mount
  useEffect(() => {
    loadPlayerDetails();
  }, [user?.id]);

  // Load reports when player data is available
  useEffect(() => {
    if (player.id) {
      loadTrainingReports();
      loadRankingReports();
      loadRankingScores();
    }
  }, [player.id]);

  // Handle ranking tab navigation - navigate to ranking overview page
  useEffect(() => {
    if (activeTab === 'ranking') {
      navigate('/player/ranking-overview', { replace: true });
    }
  }, [activeTab, navigate]);

  // Load linked coaches for "Evaluated By" labels in Training Reports
  useEffect(() => {
    const loadLinkedCoaches = async () => {
      if (!player.id) return;
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
        const resp = await fetch(`/api/players/${player.id}/coaches`, {
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        });
        const data = await safeJson(resp);
        if (resp.ok && data?.success && Array.isArray(data.data)) {
          const map: Record<string, string> = {};
          data.data.forEach((c: any) => {
            if (!c?.id) return;
            const id = String(c.id);
            const name =
              (c.name && String(c.name).trim()) ||
              `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
              c.coachIdCode ||
              `Coach ${id.substring(0, 8)}`;
            map[id] = name;
          });
          setLinkedCoachNameById(map);
        }
      } catch (e) {
        // Non-fatal; Training Reports may already include report.coachName
        console.error('Error loading linked coaches for dashboard:', e);
      }
    };
    loadLinkedCoaches();
  }, [player.id]);

  // Load coach options
  const loadCoachOptions = async () => {
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`/api/performance/fitness/player/${player.id}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      const data = await safeJson(response);
      if (response.ok && data.success) {
        const allEntries = data.data || [];
        const coachEntriesMap = new Map<string, any[]>();
        
        allEntries.forEach((entry: any) => {
          if (entry.coachId && entry.coachName) {
            if (!coachEntriesMap.has(entry.coachId)) {
              coachEntriesMap.set(entry.coachId, []);
            }
            coachEntriesMap.get(entry.coachId)!.push(entry);
          }
        });
        
        const coachOptionsList = Array.from(coachEntriesMap.entries()).map(([coachId, entries]) => {
          const firstEntry = entries[0];
          return {
            id: coachId,
            name: firstEntry.coachName
          };
        });
        
        setCoachOptions(coachOptionsList);
        
        // Set initial selection to first coach if available
        if (coachOptionsList.length > 0) {
          setSelectedCoach(coachOptionsList[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading coach options:', error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'performance') {
      // Pass selectedCoach as URL parameter - navigate to new sport-based route
      navigate(`/player/performance/Badminton/skill-assessment-high-level${selectedCoach ? `?coach=${selectedCoach}` : ''}`);
    }
  };

  // Load coach options on mount
  useEffect(() => {
    if (player.id) {
      loadCoachOptions();
    }
  }, [player.id]);

  const drawerMobile = <PlayerSidebar />;
  const drawerDesktop = (
    <PlayerSidebar
      collapsed={sidebarCollapsed}
      onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
      showCollapseToggle
    />
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <PlayerProfile 
            player={player} 
            onUpdate={(updatedPlayer) => {
              setPlayer(updatedPlayer);
              // Reload player details to ensure all fields are up to date
              loadPlayerDetails();
            }}
          />
        );
      case 'ranking':
        // Navigate directly to ranking overview page
        // Navigation is handled by useEffect hook outside this function
        return null;
      case 'certificates':
        return <PlayerCertificates player={player} />;
      case 'privacy':
        return <MyDataPrivacyTab />;
      case 'invoices':
        return <BillingInvoicesPanel />;
      case 'dashboard':
      default:
        return (
          <Box>
            {/* Player Level and Ranking Scores Section */}
            <Card sx={{ mb: 4, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #dbe8f6', boxShadow: '0 16px 40px rgba(15, 76, 129, 0.08)' }}>
              <CardContent>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f4c81', lineHeight: 1.15 }}>
                    Performance Dashboard
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Compare self, coach, and EncycGames evaluations across every sport.
                  </Typography>
                </Box>

                {/* Ranking Scores by Sport – one card per sport, table without header inside each card */}
                {loadingRankingScores ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  (() => {
                    const getSportTheme = (sportName: string) => {
                      switch (sportName?.toLowerCase()) {
                        case 'badminton':
                          return { main: '#1565c0', light: '#e3f2fd', border: '#90caf9', gradient: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 58%, #dbeafe 100%)' };
                        case 'cricket':
                          return { main: '#2e7d32', light: '#e8f5e9', border: '#a5d6a7', gradient: 'linear-gradient(135deg, #e8f5e9 0%, #ffffff 58%, #dcfce7 100%)' };
                        case 'tennis':
                          return { main: '#827717', light: '#f9fbe7', border: '#dce775', gradient: 'linear-gradient(135deg, #f9fbe7 0%, #ffffff 58%, #fef9c3 100%)' };
                        case 'football':
                          return { main: '#00897b', light: '#e0f2f1', border: '#80cbc4', gradient: 'linear-gradient(135deg, #e0f2f1 0%, #ffffff 58%, #ccfbf1 100%)' };
                        case 'ice hockey':
                          return { main: '#5e35b1', light: '#ede7f6', border: '#b39ddb', gradient: 'linear-gradient(135deg, #ede7f6 0%, #ffffff 58%, #e9d5ff 100%)' };
                        case 'athletics':
                          return { main: '#ef6c00', light: '#fff3e0', border: '#ffcc80', gradient: 'linear-gradient(135deg, #fff3e0 0%, #ffffff 58%, #ffedd5 100%)' };
                        default:
                          return { main: '#1976d2', light: '#e3f2fd', border: '#90caf9', gradient: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 58%, #dbeafe 100%)' };
                      }
                    };
                    const evalTheme: Record<'Self' | 'Coach' | 'EncycGames', { main: string; light: string; border: string; title: string }> = {
                      Self: { main: '#2e7d32', light: '#ecfdf5', border: '#86efac', title: 'Self Score' },
                      Coach: { main: '#6d28d9', light: '#f3e8ff', border: '#c4b5fd', title: 'Coach Score' },
                      EncycGames: { main: '#d97706', light: '#fffbeb', border: '#fcd34d', title: 'EncycGames Score' },
                    };
                    const getSportIcon = (sportName: string) => {
                      const theme = getSportTheme(sportName);
                      switch (sportName?.toLowerCase()) {
                        case 'badminton':
                          return <BadmintonIcon sx={{ fontSize: 28, color: theme.main }} />;
                        case 'cricket':
                          return <CricketIcon sx={{ fontSize: 28, color: theme.main }} />;
                        case 'tennis':
                          return <TennisIcon sx={{ fontSize: 28, color: theme.main }} />;
                        case 'football':
                          return <FootballIcon sx={{ fontSize: 28, color: theme.main }} />;
                        case 'ice hockey':
                          return <IceHockeyIcon sx={{ fontSize: 28, color: theme.main }} />;
                        case 'athletics':
                          return <AthleticsIcon sx={{ fontSize: 28, color: theme.main }} />;
                        default:
                          return <AthleticsIcon sx={{ fontSize: 28, color: theme.main }} />;
                      }
                    };
                    const formatScore = (achieved: number, max: number) =>
                      max > 0 ? `${achieved}/${max}` : `${achieved}`;
                    const formatPeriod = (dateStr: string | null | undefined): string => {
                      if (!dateStr) return '–';
                      const d = new Date(dateStr);
                      if (isNaN(d.getTime())) return String(dateStr);
                      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      return `${months[d.getMonth()]} ${d.getFullYear()}`;
                    };
                    const evalLabels: Record<string, string> = {
                      'self-evaluation': 'Self',
                      'coach-academy': 'Coach',
                      'practical-test': 'EncycGames'
                    };
                    const normalizeCategoryLabel = (sport: string, raw?: string | null) => {
                      const value = String(raw || '').trim().toLowerCase();
                      if (isShootingSport(sport)) {
                        if (!value || value === 'strokes-shots' || value === 'strokes and shots') return 'Shooting';
                      }
                      return raw && String(raw).trim().length > 0 ? String(raw) : 'Strokes and Shots';
                    };

                    const inferDefaultRole = (sport: string) => {
                      if (sport === 'Badminton') return 'Singles/Doubles';
                      if (sport === 'Football') return 'Winger/Midfielder/Defender/Striker';
                      if (sport === 'Ice Hockey') return 'Forward/Defense/Goalie';
                      return 'General';
                    };

                    // Build flat rows for one sport
                    const buildRowsForSport = (sport: string) => {
                      const subcategoryRows = rankingScoresBySubcategory.filter((r: any) => r.sport === sport);
                      const rows: Array<{ evalLabel: string; categoryLabel: string; roleLabel: string; levelLabel: string; gradeWithCategory: string; pts: string; periodLabel: string }> = [];

                      if (subcategoryRows.length > 0) {
                        for (const row of subcategoryRows) {
                          const catKeys = ['self-evaluation', 'coach-academy', 'practical-test'] as const;
                          for (const key of catKeys) {
                            const cat = row[key];
                            if (!cat?.hasData && !cat?.grade) continue;
                            const levelText = cat?.levelLabel || 'Level 1 - Beginner';
                            const categoryText = normalizeCategoryLabel(sport, row.subcategoryLabel);
                            rows.push({
                              evalLabel: evalLabels[key] || key,
                              categoryLabel: categoryText,
                              roleLabel: String(cat?.roleLabel || inferDefaultRole(sport)),
                              levelLabel: levelText,
                              gradeWithCategory: cat.grade ? `Grade ${cat.grade}` : '–',
                              pts: `${formatScore(Number(cat?.totalScore ?? 0), Number(cat?.maxScore ?? 0))} pts`,
                              periodLabel: formatPeriod(cat?.evaluationDate ?? null)
                            });
                          }
                        }
                      } else {
                        const categories = rankingScores[sport];
                        if (!categories) return rows;
                        const catKeys = ['self-evaluation', 'coach-academy', 'practical-test'] as const;
                        for (const key of catKeys) {
                          const cat = categories[key];
                          if (!cat?.hasData && !cat?.grade) continue;
                          rows.push({
                            evalLabel: evalLabels[key] || key,
                            categoryLabel: normalizeCategoryLabel(sport, null),
                            roleLabel: String(cat?.roleLabel || inferDefaultRole(sport)),
                            levelLabel: cat?.levelLabel || 'Level 1 - Beginner',
                            gradeWithCategory: cat.grade ? `Grade ${cat.grade}` : '–',
                            pts: `${formatScore(Number(cat?.totalScore ?? 0), Number(cat?.maxScore ?? 0))} pts`,
                            periodLabel: formatPeriod(cat?.evaluationDate ?? null)
                          });
                        }
                      }
                      return rows;
                    };

                    const gradeFromRow = (r: { gradeWithCategory: string }) => {
                      const m = r.gradeWithCategory.match(/Grade\s+([A-Z+])\s*(?:\(|$)/i);
                      return m ? m[1] : '';
                    };
                    const enabledSports = CORE_SPORTS;
                    const sportsToRender = Array.from(new Set([...enabledSports, ...Object.keys(rankingScores || {})]));
                    const downloadSummaryPdf = async () => {
                      const sports = sportsToRender.map((sport) => {
                        const tableRows = buildRowsForSport(sport);
                        return {
                          sport,
                          isPrimarySport: sport === player.primarySport,
                          isSecondarySport: sport === player.secondarySport,
                          self: tableRows
                            .filter((r) => r.evalLabel === 'Self')
                            .map((r) => ({
                              categoryLabel: r.categoryLabel,
                              roleLabel: r.roleLabel,
                              periodLabel: r.periodLabel,
                              levelLabel: r.levelLabel,
                              gradeWithCategory: r.gradeWithCategory,
                              pts: r.pts,
                            })),
                          coach: tableRows
                            .filter((r) => r.evalLabel === 'Coach')
                            .map((r) => ({
                              categoryLabel: r.categoryLabel,
                              roleLabel: r.roleLabel,
                              periodLabel: r.periodLabel,
                              levelLabel: r.levelLabel,
                              gradeWithCategory: r.gradeWithCategory,
                              pts: r.pts,
                            })),
                          encycgames: tableRows
                            .filter((r) => r.evalLabel === 'EncycGames')
                            .map((r) => ({
                              categoryLabel: r.categoryLabel,
                              roleLabel: r.roleLabel,
                              periodLabel: r.periodLabel,
                              levelLabel: r.levelLabel,
                              gradeWithCategory: r.gradeWithCategory,
                              pts: r.pts,
                            })),
                        };
                      });

                      const playerName = `${player?.firstName || ''} ${player?.lastName || ''}`.trim() || user?.email || 'Player';
                      await generateDashboardReportCardPDF({
                        playerName,
                        academyName: player?.academyName || 'N/A',
                        sports,
                      });
                    };

                    return (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="outlined" size="small" startIcon={<PdfIcon />} onClick={downloadSummaryPdf}>
                              Download Report Card
                            </Button>
                          </Box>
                        </Grid>
                        {sportsToRender.map((sport) => {
                          const hasData = Object.values(rankingScores[sport] || {}).some((c: any) => c?.hasData || c?.grade) || buildRowsForSport(sport).length > 0;
                          const isPrimarySport = sport === player.primarySport;
                          const isSecondarySport = sport === player.secondarySport;
                          const tableRows = buildRowsForSport(sport);
                          const sportTheme = getSportTheme(sport);
                          const rowsByType = {
                            Self: tableRows.filter((r) => r.evalLabel === 'Self'),
                            Coach: tableRows.filter((r) => r.evalLabel === 'Coach'),
                            EncycGames: tableRows.filter((r) => r.evalLabel === 'EncycGames'),
                          };

                          return (
                            <Grid item xs={12} key={sport}>
                              <Card
                                sx={{
                                  position: 'relative',
                                  overflow: 'hidden',
                                  borderRadius: 3,
                                  border: `1px solid ${sportTheme.border}`,
                                  bgcolor: '#fff',
                                  background: sportTheme.gradient,
                                  boxShadow: hasData ? `0 14px 32px ${sportTheme.main}18` : 'none',
                                  opacity: hasData ? 1 : 0.85,
                                  '&:before': {
                                    content: '""',
                                    position: 'absolute',
                                    width: 140,
                                    height: 140,
                                    right: -45,
                                    top: -55,
                                    borderRadius: '50%',
                                    border: `24px solid ${sportTheme.main}18`,
                                  },
                                }}
                              >
                                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'space-between', flexWrap: 'nowrap', position: 'relative' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                      <Box
                                        sx={{
                                          width: 42,
                                          height: 42,
                                          borderRadius: 2,
                                          bgcolor: sportTheme.light,
                                          border: `1px solid ${sportTheme.border}`,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                      >
                                        {getSportIcon(sport)}
                                      </Box>
                                      <Typography variant="h6" sx={{ fontWeight: 900, color: sportTheme.main, whiteSpace: 'nowrap' }}>
                                        {sport}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                                      {isPrimarySport && (
                                        <Chip label="Primary" size="small" color="primary" sx={{ fontSize: '0.7rem', height: 20 }} />
                                      )}
                                      {isSecondarySport && (
                                        <Chip label="Secondary" size="small" color="secondary" sx={{ fontSize: '0.7rem', height: 20 }} />
                                      )}
                                    </Box>
                                  </Box>
                                  <Grid container spacing={1.25}>
                                    {(['Self', 'Coach', 'EncycGames'] as const).map((type) => {
                                      const sectionTheme = evalTheme[type];
                                      return (
                                      <Grid item xs={12} md={4} key={`${sport}-${type}`}>
                                        <Paper
                                          variant="outlined"
                                          sx={{
                                            p: 1.25,
                                            borderRadius: 2,
                                            height: '100%',
                                            bgcolor: sectionTheme.light,
                                            borderColor: sectionTheme.border,
                                            borderTop: `5px solid ${sectionTheme.main}`,
                                            boxShadow: `0 10px 22px ${sectionTheme.main}12`,
                                          }}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.9 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 900, color: sectionTheme.main }}>
                                              {sectionTheme.title}
                                            </Typography>
                                          </Box>
                                          {rowsByType[type].length === 0 ? (
                                            <Box sx={{ border: `1px dashed ${sectionTheme.border}`, borderRadius: 1.5, p: 1, bgcolor: 'rgba(255,255,255,0.65)' }}>
                                              <Typography variant="caption" color="text.secondary">No data yet</Typography>
                                            </Box>
                                          ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                              {rowsByType[type].map((r, idx) => (
                                                <Box
                                                  key={`${sport}-${type}-${idx}`}
                                                  sx={{
                                                    border: `1px solid ${sectionTheme.border}`,
                                                    borderLeft: `4px solid ${sectionTheme.main}`,
                                                    borderRadius: 1.5,
                                                    p: 0.9,
                                                    bgcolor: '#fff',
                                                  }}
                                                >
                                                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, color: '#102a43' }}>{r.categoryLabel}</Typography>
                                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Role: {r.roleLabel || inferDefaultRole(sport)}</Typography>
                                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{r.periodLabel} | {r.levelLabel}</Typography>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
                                                    <Chip
                                                      size="small"
                                                      label={r.gradeWithCategory}
                                                      sx={{
                                                        height: 22,
                                                        fontSize: '0.68rem',
                                                        fontWeight: 900,
                                                        bgcolor: r.gradeWithCategory !== '–' ? `${getGradeColor(gradeFromRow(r))}18` : '#f1f5f9',
                                                        color: r.gradeWithCategory !== '–' ? getGradeColor(gradeFromRow(r)) : 'text.secondary',
                                                      }}
                                                    />
                                                    <Typography variant="caption" sx={{ fontWeight: 900, color: sectionTheme.main }}>{r.pts}</Typography>
                                                  </Box>
                                                </Box>
                                              ))}
                                            </Box>
                                          )}
                                        </Paper>
                                      </Grid>
                                      );
                                    })}
                                  </Grid>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    );
                  })()
                )}
              </CardContent>
            </Card>

            {/* Ranking Reports */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Ranking Reports
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AiIcon />}
                    onClick={() => navigate('/player/recommendations')}
                  >
                    AI Assistant
                  </Button>
                </Box>
                {loadingReports ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : rankingReports.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'primary.light' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Sport</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Score (%)</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Score Details</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Skills</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Evaluation Status</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rankingReports.map((report, index) => (
                          <TableRow 
                            key={`${report.sport ?? 'sport'}-${report.categoryId ?? 'cat'}-${index}`} 
                            hover
                            sx={{ 
                              opacity: report.hasData ? 1 : 0.6 
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip 
                                  label={report.sport || player.primarySport || 'N/A'} 
                                  color={report.isPrimarySport ? 'primary' : report.isSecondarySport ? 'secondary' : 'default'}
                                  size="small"
                                />
                                {report.isPrimarySport && (
                                  <Typography variant="caption" color="text.secondary">
                                    (Primary)
                                  </Typography>
                                )}
                                {report.isSecondarySport && !report.isPrimarySport && (
                                  <Typography variant="caption" color="text.secondary">
                                    (Secondary)
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {report.categoryLabel}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {report.evaluationDate ? (() => {
                                // Convert evaluation date to period label
                                const evalDate = new Date(report.evaluationDate);
                                const year = evalDate.getFullYear();
                                const month = evalDate.getMonth() + 1;
                                const day = evalDate.getDate();
                                
                                // Determine period based on date
                                if (month === 12 && day === 31) {
                                  return `Yearly ${year}`;
                                } else if (month === 6 && day === 30) {
                                  return `H1 ${year}`;
                                } else if (month === 12 && day === 30) {
                                  return `H2 ${year}`;
                                } else if (month <= 3) {
                                  return `Q1 ${year}`;
                                } else if (month <= 6) {
                                  return `Q2 ${year}`;
                                } else if (month <= 9) {
                                  return `Q3 ${year}`;
                                } else {
                                  return `Q4 ${year}`;
                                }
                              })() : (
                                <Typography variant="body2" color="text.secondary">N/A</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {report.hasData ? (
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: getGradeColor(report.grade || 'C') }}>
                                    {report.grade || 'C'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {report.score || 0} pts ({report.percentage}%)
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">N/A</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {report.hasData ? (
                                <Typography variant="body2">
                                  {report.score.toFixed(2)} / {report.maxScore.toFixed(2)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {report.hasData ? (
                                <Typography variant="body2">{report.totalStrokes} strokes</Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                // Determine evaluation status based on categoryId
                                if (report.categoryId === 'coach-academy') {
                                  return <Chip label="Coach Evaluation" color="success" size="small" sx={{ fontWeight: 600 }} />;
                                } else if (report.categoryId === 'practical-test') {
                                  return <Chip label="Admin Evaluation" color="warning" size="small" sx={{ fontWeight: 600 }} />;
                                } else if (report.categoryId === 'self-evaluation') {
                                  return <Chip label="Self Evaluation" color="info" size="small" sx={{ fontWeight: 600 }} />;
                                }
                                return <Chip label="N/A" color="default" size="small" />;
                              })()}
                            </TableCell>
                            <TableCell>
                              {report.hasData ? (
                                <Chip label="Available" color="success" size="small" />
                              ) : (
                                <Chip label="No Data" color="default" size="small" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  variant={report.hasData ? "contained" : "outlined"}
                                  size="small"
                                  startIcon={<RankingIcon />}
                                  onClick={() => {
                                    // v2 reportId navigation (direct fetch)
                                    if ((report as any).reportId) {
                                      navigate(`/player/ranking-report/${(report as any).reportId}`);
                                      return;
                                    }
                                    // Pass evaluation date as URL parameter if available
                                    const params = new URLSearchParams();
                                    if (report.evaluationDate) {
                                      // Ensure date is in YYYY-MM-DD format WITHOUT timezone shifting.
                                      // - If backend sends a plain date "YYYY-MM-DD", keep it as-is.
                                      // - If backend sends an ISO timestamp, convert to LOCAL YYYY-MM-DD (so 2025-12-31 doesn't become 2025-12-30).
                                      const raw = String(report.evaluationDate);
                                      let dateStr = raw;
                                      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                                        dateStr = raw;
                                      } else {
                                        const d = new Date(raw);
                                        const yyyy = d.getFullYear();
                                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                                        const dd = String(d.getDate()).padStart(2, '0');
                                        dateStr = `${yyyy}-${mm}-${dd}`;
                                      }
                                      params.set('date', dateStr);
                                      // Also pass sport so the ranking page reads from the correct sport table
                                      // (especially important if the ranking page doesn't have player sports loaded yet)
                                      params.set('sport', report.sport || player.primarySport || 'Badminton');
                                      console.log('[Dashboard] Navigating with date:', dateStr, '(original:', report.evaluationDate, ')');
                                    }
                                    const queryString = params.toString();
                                    const defaultSport = report.sport || player.primarySport || 'Badminton';
                                    const defaultLevel = 'beginner';
                                    const defaultSubcategory = getDefaultRankingSubcategory(defaultSport);
                                    navigate(`/player/ranking-evaluation/${report.categoryId}?sport=${defaultSport}&level=${defaultLevel}&subcategory=${defaultSubcategory}${queryString ? `&${queryString}` : ''}`);
                                  }}
                                  disabled={!report.hasData}
                                >
                                  View
                                </Button>
                                {report.hasData && (
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<PdfIcon />}
                                    onClick={async () => {
                                      try {
                                        if (!report.evaluationDate) {
                                          alert('No evaluation date available for this report.');
                                          return;
                                        }

                                        // Normalize date format
                                        let dateStr = report.evaluationDate;
                                        if (dateStr.includes('T')) {
                                          dateStr = dateStr.split('T')[0];
                                        }
                                        if (dateStr.includes(' ')) {
                                          dateStr = dateStr.split(' ')[0];
                                        }
                                        if (dateStr.includes('+') || dateStr.includes('Z')) {
                                          dateStr = dateStr.split('+')[0].split('Z')[0];
                                        }

                                        console.log('[Dashboard] Fetching ranking evaluation data...', {
                                          categoryId: report.categoryId,
                                          playerId: player.id,
                                          date: dateStr
                                        });

                                        // Fetch submitted evaluation data for the specific evaluation date
                                        // Evaluation periods are unique (monthly/quarterly/yearly), so query by exact date
                                        const apiUrl = `/api/ranking-evaluations/category/${report.categoryId}/player/${player.id}?date=${encodeURIComponent(dateStr)}&sport=${encodeURIComponent(report.sport || player.primarySport || 'Badminton')}`;
                                        console.log('[Dashboard] Download - Fetching from:', apiUrl);
                                        
                                        const response = await fetch(apiUrl);
                                        
                                        if (!response.ok) {
                                          const errorText = await response.text();
                                          console.error('[Dashboard] Download - API error:', response.status, errorText);
                                          throw new Error(`Failed to fetch evaluation data: ${response.status} ${response.statusText}`);
                                        }

                                        const data = await response.json();
                                        console.log('[Dashboard] Download - API response:', {
                                          success: data.success,
                                          dataCount: data.data?.length || 0,
                                          hasData: !!data.data && data.data.length > 0
                                        });

                                        if (!data.success) {
                                          alert(`Error: ${data.message || 'Failed to fetch evaluation data'}`);
                                          return;
                                        }

                                        // Filter to only submitted evaluations
                                        const submittedData = data.data.filter((item: any) => item.submitted === true || item.submitted === 'true' || item.submitted === 1);

                                        if (!submittedData || submittedData.length === 0) {
                                          alert(`No submitted evaluation data found for date ${dateStr}.`);
                                          return;
                                        }

                                        // Get coach name
                                        let coachName = 'N/A';
                                        try {
                                          const coachResponse = await fetch(`/api/coaches`);
                                          const coachData = await coachResponse.json();
                                          if (coachData.success && coachData.data && coachData.data.length > 0) {
                                            const coach = coachData.data[0];
                                            if (coach && coach.user) {
                                              coachName = `${coach.user.firstName || ''} ${coach.user.lastName || ''}`.trim() || 'N/A';
                                            }
                                          }
                                        } catch (err) {
                                          console.error('Error loading coach name:', err);
                                        }

                                        // Fetch the stroke types in the correct order (same as UI and manual form)
                                        const strokeTypesResponse = await fetch('/api/admin/ranking-stroke-types');
                                        if (!strokeTypesResponse.ok) {
                                          throw new Error('Failed to load stroke types');
                                        }
                                        const strokeTypesData = await strokeTypesResponse.json();
                                        
                                        if (!strokeTypesData.success || !strokeTypesData.data) {
                                          throw new Error('No stroke types found');
                                        }

                                        // Sort by displayOrder (same as UI and manual form)
                                        const orderedStrokeTypes = strokeTypesData.data
                                          .filter((st: any) => st.isActive !== false)
                                          .sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0));

                                        // Create a map of submitted ratings by code
                                        const submittedRatingsMap = new Map<string, any>();
                                        submittedData
                                          .filter((stroke: any) => stroke.rating && stroke.rating > 0)
                                          .forEach((stroke: any) => {
                                            submittedRatingsMap.set(stroke.code, stroke);
                                          });

                                        // Map strokes in the same order as UI and manual form (by displayOrder)
                                        const strokes = orderedStrokeTypes
                                          .map((strokeType: any) => {
                                            const submittedData = submittedRatingsMap.get(strokeType.code);
                                            if (!submittedData) return null; // Only include rated strokes
                                            return {
                                              code: strokeType.code,
                                              courtPosition: strokeType.courtPosition || '',
                                              halfSide: strokeType.halfSide || '',
                                              strokeType: strokeType.strokeType || '',
                                              strokeAction: strokeType.strokeAction || '',
                                              shotType: strokeType.shotType || '',
                                              placement: strokeType.placement || '',
                                              rating: submittedData.rating || 0,
                                            };
                                          })
                                          .filter((stroke: any) => stroke !== null);

                                        if (strokes.length === 0) {
                                          alert('No rated strokes found in the evaluation data.');
                                          return;
                                        }

                                        const reportData = {
                                          player,
                                          categoryId: report.categoryId,
                                          categoryLabel: report.categoryLabel,
                                          sport: report.sport || player.primarySport || 'Badminton',
                                          evaluationDate: dateStr,
                                          strokes,
                                          totalScore: report.score || 0,
                                          maxScore: report.maxScore || 0,
                                          ratedStrokes: report.totalStrokes || 0,
                                          percentage: parseFloat(report.percentage || '0'),
                                          grade: report.grade,
                                          gradeDescription: report.gradeDescription || '',
                                          coachName,
                                          submittedAt: submittedData[0]?.createdAt || new Date().toISOString(),
                                        };

                                        await generateRankingEvaluationReportPDF(reportData);
                                      } catch (error) {
                                        console.error('Error generating report:', error);
                                        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                                        alert(`Failed to generate report: ${errorMessage}`);
                                      }
                                    }}
                                    sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
                                  >
                                    Download
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No ranking reports available yet.
                  </Typography>
                )}
              </CardContent>
            </Card>

          </Box>
        );
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH }, flexShrink: { sm: 0 }, transition: 'width 0.3s' }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawerMobile}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
              transition: 'width 0.3s',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawerDesktop}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: { sm: `calc(100% - ${sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH}px)` },
          transition: 'width 0.3s',
        }}
      >
        <Container maxWidth="xl">
          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
          {playerLoadError && !loading && (
            <Alert severity="warning" sx={{ mb: 3 }} action={
              playerLoadError.includes('log in') ? (
                <Button color="inherit" size="small" onClick={() => navigate('/login')}>Log in again</Button>
              ) : (
                <Button color="inherit" size="small" onClick={() => loadPlayerDetails()}>Retry</Button>
              )
            }>
              {playerLoadError}
            </Alert>
          )}
          {showEmailVerifiedMessage && (
            <Alert 
              severity="success" 
              sx={{ mb: 3 }} 
              onClose={() => setShowEmailVerifiedMessage(false)}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Email Verified Successfully!
              </Typography>
              <Typography variant="body2">
                Your email address has been verified. You can now use all features of the platform.
              </Typography>
            </Alert>
          )}
          {renderContent()}
        </Container>
      </Box>
    </Box>
  );
};

export default PlayerDashboard;
